/**
 * In-match truck console: PGM/PVW monitors, monitor wall, side panels, deck,
 * toasts. The WebGL canvas sits BEHIND this DOM; feed areas are transparent
 * windows. Call `layout()` to get the rects the renderer should scissor into.
 */
import './console.css';
import type {
  CameraKind, CommentatorRequest, DirectorState, GradeCategory, GradeFeedback, GradeReport,
  GraphicKind, MatchConfig, PlayerProfile, WorldSnapshot,
} from '../core/contracts';
import { Emitter } from '../core/events';
import { h, setText, toggleClass, keyButton, prefersReducedMotion } from './dom';
import { bezelRect, computeConsoleLayout, holesPath, type ConsoleGeometry, type Rect } from './consoleLayout';
import { createGauge, createOdometer, createSegmentDisplay, createSparkline } from './widgets';
import {
  CATEGORY_LABELS, LETTER_COLORS, PHASE_LABELS, TIME_LABELS, WEATHER_LABELS,
  barColor, clockText, formatCompact, streakMultiplier,
} from './format';
import { CATEGORY_ORDER } from '../meta/share';

export type { Rect } from './consoleLayout';

export interface ConsoleCam { id: string; label: string; hotkey?: string; kind?: CameraKind }

export interface ConsoleLayout { program: Rect; preview: Rect; wall: { camId: string; rect: Rect }[] }

export interface ConsoleView {
  snapshot: WorldSnapshot;
  report: GradeReport;
  director: DirectorState;
  requests: CommentatorRequest[];
  config: MatchConfig;
}

/** The Q key is context-aware: the director resolves 'lower_third' to the right GraphicKind + subject. */
export type ConsoleGraphic = 'lower_third' | GraphicKind;

export type ConsoleActions = {
  /** Hot-punch camera to PGM (during replay the integrator should map this to a replay angle cut). */
  punch: string;
  preview: string;
  take: undefined;
  mix: undefined;
  /** Toggle: roll latest replay-worthy moment, or return to live. */
  replay: undefined;
  graphic: ConsoleGraphic;
  clearGraphics: undefined;
  scorebug: undefined;
  pause: undefined;
  /** Booth request focused via click (id). */
  boothFocus: number;
};

export interface ConsoleOptions {
  cams: ConsoleCam[];
  /** 'hot' (default): click = punch, shift-click = preview. 'preview' swaps them. */
  punchMode?: 'hot' | 'preview';
}

export interface ConsoleStats { peakViewers: number; bestStreak: number; viewerHistory: number[] }

export interface TruckConsole {
  readonly el: HTMLElement;
  readonly actions: Emitter<ConsoleActions>;
  /** Container exactly over the PGM feed for broadcast graphics (container-type: size → use cqw/cqh). */
  readonly programOverlay: HTMLElement;
  layout(): ConsoleLayout;
  /** Called after every relayout (resize / cams changed). Returns an unsubscribe. */
  onLayout(fn: (l: ConsoleLayout) => void): () => void;
  update(view: ConsoleView): void;
  toast(f: GradeFeedback, opts?: { big?: boolean }): void;
  setCams(cams: ConsoleCam[]): void;
  setPunchMode(mode: 'hot' | 'preview'): void;
  /** Visual key-press feedback for keyboard input, e.g. indicate('Space'), indicate('Q'), indicate('3'). */
  indicate(key: string): void;
  /** Cycle the booth request focus (Tab); returns the focused request id. */
  focusBooth(dir: 1 | -1): number | null;
  /** Currently focused booth request id (Tab / click), if any. */
  focusedRequest(): number | null;
  stats(): ConsoleStats;
  /** Reset per-match state (viewer history, peaks, toasts). */
  reset(): void;
  destroy(): void;
}

const GRAPHIC_KEYS: { key: string; label: string; action: () => [keyof ConsoleActions, ConsoleGraphic | undefined]; kinds: GraphicKind[] }[] = [
  { key: 'Q', label: 'LOWER 3RD', action: () => ['graphic', 'lower_third'], kinds: ['goal_lower_third', 'player_lower_third', 'card_lower_third', 'sub_lower_third', 'player_stat_card'] },
  { key: 'W', label: 'STATS', action: () => ['graphic', 'match_stats'], kinds: ['match_stats'] },
  { key: 'E', label: 'POSSESSION', action: () => ['graphic', 'possession_bar'], kinds: ['possession_bar'] },
  { key: 'S', label: 'SCOREBUG', action: () => ['scorebug', undefined], kinds: ['scorebug'] },
  { key: 'A', label: 'CLEAR', action: () => ['clearGraphics', undefined], kinds: [] },
];

const WANT_GRAPHIC_KEY: Partial<Record<GraphicKind, string>> = {
  goal_lower_third: 'Q', player_lower_third: 'Q', card_lower_third: 'Q', sub_lower_third: 'Q', player_stat_card: 'Q',
  match_stats: 'W', possession_bar: 'E', scorebug: 'S',
};
const GRAPHIC_NAMES: Partial<Record<GraphicKind, string>> = {
  goal_lower_third: 'GOAL LOWER THIRD', player_lower_third: 'PLAYER LOWER THIRD', card_lower_third: 'CARD LOWER THIRD',
  sub_lower_third: 'SUB LOWER THIRD', player_stat_card: 'PLAYER STATS', match_stats: 'MATCH STATS', possession_bar: 'POSSESSION',
  scorebug: 'SCOREBUG', team_lineup: 'LINE-UPS',
};

interface MonitorEls { root: HTMLElement; screen: HTMLElement; umd: HTMLElement; tally: HTMLElement; title: HTMLElement; sub: HTMLElement }

function monitor(cls: string, tag: string): MonitorEls {
  const tally = h('span', { class: 'sd-mon__tally' });
  const title = h('span', { class: 'sd-mon__title' });
  const sub = h('span', { class: 'sd-mon__sub' });
  const screen = h('div', { class: 'sd-mon__screen' }, [h('div', { class: 'sd-mon__glass' })]);
  const umd = h('div', { class: 'sd-mon__umd' }, [tally, h('span', { class: 'sd-mon__tag', text: tag }), title, sub]);
  const root = h('div', { class: `sd-mon ${cls}` }, [screen, umd]);
  return { root, screen, umd, tally, title, sub };
}

function place(el: HTMLElement, r: Rect): void {
  el.style.transform = `translate(${r.x}px, ${r.y}px)`;
  el.style.width = `${r.w}px`;
  el.style.height = `${r.h}px`;
}

export function createConsole(root: HTMLElement, opts: ConsoleOptions): TruckConsole {
  const actions = new Emitter<ConsoleActions>();
  let cams = opts.cams.slice();
  let punchMode = opts.punchMode ?? 'hot';
  let geo: ConsoleGeometry | null = null;
  let layoutCache: ConsoleLayout | null = null;
  const layoutListeners = new Set<(l: ConsoleLayout) => void>();

  const el = h('div', { class: 'sd-console', attrs: { 'data-ui': 'console' } });
  const plate = h('div', { class: 'sd-plate' });

  // ---------------------------------------------------------------- top bar
  const compEl = h('span', { class: 'sd-top__comp' });
  const venueEl = h('span', { class: 'sd-top__venue' });
  const onAir = h('div', { class: 'sd-onair' }, [h('span', { class: 'sd-onair__lamp' }), h('span', { text: 'ON AIR' })]);
  const viewersOdo = createOdometer('sd-viewers__num');
  const viewersArrow = h('span', { class: 'sd-viewers__arrow', text: '▲' });
  const viewersDelta = h('span', { class: 'sd-viewers__delta' });
  const spark = createSparkline(140, 30, 'sd-viewers__spark');
  const pauseBtn = h('button', { class: 'sd-iconbtn', attrs: { type: 'button', title: 'Pause (Esc)' }, on: { click: () => actions.emit('pause', undefined) } },
    [h('span', { class: 'sd-iconbtn__glyph', text: '❚❚' }), h('span', { text: 'PAUSE' }), h('kbd', { text: 'ESC' })]);
  const top = h('header', { class: 'sd-top' }, [
    h('div', { class: 'sd-top__brand' }, [h('span', { class: 'sd-brand-dot' }), h('span', { class: 'sd-top__logo', text: 'SPORTS DIRECTOR' })]),
    h('div', { class: 'sd-top__meta' }, [compEl, venueEl]),
    onAir,
    h('div', { class: 'sd-viewers' }, [
      h('div', { class: 'sd-viewers__label', text: 'VIEWERS' }),
      h('div', { class: 'sd-viewers__row' }, [viewersOdo.el, viewersArrow, viewersDelta]),
      spark.el,
    ]),
    pauseBtn,
  ]);

  // ---------------------------------------------------------------- PGM / PVW
  const pgm = monitor('sd-mon--pgm is-pgm', 'PGM');
  const pvw = monitor('sd-mon--pvw is-pvw', 'PVW');
  pvw.screen.title = 'Click to TAKE';
  pvw.screen.addEventListener('click', () => actions.emit('take', undefined));
  pvw.screen.setAttribute('role', 'button');
  const programOverlay = h('div', { class: 'sd-pgm-overlay', attrs: { 'data-layer': 'broadcast-graphics' } });
  const toastLayer = h('div', { class: 'sd-toasts' });
  const bigLayer = h('div', { class: 'sd-bigtoast-layer' });

  // ---------------------------------------------------------------- deck (under PVW)
  const takeBtn = keyButton({ label: 'TAKE', key: 'SPACE', cls: 'sd-key--take', title: 'Cut PVW → PGM', onClick: () => actions.emit('take', undefined) });
  const mixBtn = keyButton({ label: 'MIX', key: 'M', cls: 'sd-key--mix', title: '0.5s dissolve PVW → PGM', onClick: () => actions.emit('mix', undefined) });
  const replayBtn = keyButton({ label: 'REPLAY', key: 'R', cls: 'sd-key--replay', title: 'Roll replay / back to live', onClick: () => actions.emit('replay', undefined) });
  const rpLamp = h('span', { class: 'sd-rp__lamp' });
  const rpState = h('span', { class: 'sd-rp__state', text: 'LIVE' });
  const rpFill = h('div', { class: 'sd-rp__fill' });
  const rpHead = h('div', { class: 'sd-rp__head' });
  const rpSpeed = h('span', { class: 'sd-rp__speed', text: '1.00×' });
  const rpCam = h('span', { class: 'sd-rp__cam', text: '—' });
  const replayPanel = h('div', { class: 'sd-rp' }, [
    h('div', { class: 'sd-rp__top' }, [rpLamp, rpState, rpCam, rpSpeed]),
    h('div', { class: 'sd-rp__track' }, [rpFill, rpHead]),
  ]);
  const gfxButtons = GRAPHIC_KEYS.map((g) => {
    const b = keyButton({ label: g.label, key: g.key, cls: `sd-key--gfx sd-key--${g.key.toLowerCase()}`, onClick: () => {
      const [a, p] = g.action();
      actions.emit(a, p as never);
    } });
    return { def: g, el: b };
  });
  const deck = h('div', { class: 'sd-deck' }, [
    h('div', { class: 'sd-deck__group sd-deck__mix' }, [h('div', { class: 'sd-deck__label', text: 'M/E 1' }), h('div', { class: 'sd-deck__row' }, [takeBtn, mixBtn])]),
    h('div', { class: 'sd-deck__group sd-deck__replay' }, [h('div', { class: 'sd-deck__label', text: 'EVS · REPLAY' }), h('div', { class: 'sd-deck__row' }, [replayBtn, replayPanel])]),
    h('div', { class: 'sd-deck__group sd-deck__gfx' }, [h('div', { class: 'sd-deck__label', text: 'GRAPHICS' }), h('div', { class: 'sd-deck__row' }, gfxButtons.map((g) => g.el))]),
  ]);

  // ---------------------------------------------------------------- wall
  const wallEl = h('div', { class: 'sd-wall' });
  let tiles: { cam: ConsoleCam; mon: MonitorEls }[] = [];
  const buildWall = () => {
    wallEl.textContent = '';
    tiles = cams.map((cam, i) => {
      const mon = monitor('sd-mon--tile', cam.hotkey ?? String(i + 1));
      mon.title.textContent = cam.label;
      mon.sub.textContent = cam.id;
      if (cam.hotkey) mon.umd.append(h('kbd', { class: 'sd-mon__key', text: cam.hotkey }));
      mon.screen.setAttribute('role', 'button');
      mon.screen.title = `${cam.id} ${cam.label} — click: ${punchMode === 'hot' ? 'PUNCH' : 'PREVIEW'} · shift-click: ${punchMode === 'hot' ? 'PREVIEW' : 'PUNCH'}`;
      mon.root.dataset.cam = cam.id;
      mon.screen.addEventListener('click', (e) => {
        const toPreview = (punchMode === 'hot') === e.shiftKey;
        actions.emit(toPreview ? 'preview' : 'punch', cam.id);
        flash(mon.root);
      });
      wallEl.append(mon.root);
      return { cam, mon };
    });
  };

  // ---------------------------------------------------------------- side column
  const homeChip = h('span', { class: 'sd-team__chip' });
  const awayChip = h('span', { class: 'sd-team__chip' });
  const homeName = h('span', { class: 'sd-team__name' });
  const awayName = h('span', { class: 'sd-team__name' });
  const homeScore = h('span', { class: 'sd-score__n' });
  const awayScore = h('span', { class: 'sd-score__n' });
  const clock = createSegmentDisplay('88:88', 'sd-clock');
  const halfEl = h('span', { class: 'sd-half', text: '1ST' });
  const phaseEl = h('span', { class: 'sd-phase', text: 'PRE-MATCH' });
  const intensityFill = h('div', { class: 'sd-intensity__fill' });
  const matchCard = h('section', { class: 'sd-card sd-match' }, [
    h('div', { class: 'sd-card__head' }, [h('span', { text: 'MATCH' }), phaseEl]),
    h('div', { class: 'sd-scoreline' }, [
      h('div', { class: 'sd-team' }, [homeChip, homeName]),
      h('div', { class: 'sd-score' }, [homeScore, h('span', { class: 'sd-score__sep', text: '–' }), awayScore]),
      h('div', { class: 'sd-team sd-team--away' }, [awayName, awayChip]),
    ]),
    h('div', { class: 'sd-clockrow' }, [h('div', { class: 'sd-clockwell' }, [clock.el]), halfEl]),
    h('div', { class: 'sd-intensity' }, [h('span', { class: 'sd-intensity__label', text: 'INTENSITY' }), h('div', { class: 'sd-intensity__track' }, [intensityFill])]),
  ]);

  const gauge = createGauge();
  const multEl = h('div', { class: 'sd-mult__x', text: '×1' });
  const pips = [0, 1, 2, 3, 4].map(() => h('span', { class: 'sd-mult__pip' }));
  const streakEl = h('span', { class: 'sd-mult__streak', text: '0' });
  const pointsEl = h('span', { class: 'sd-points__n', text: '0' });
  const ratingCard = h('section', { class: 'sd-card sd-rating' }, [
    h('div', { class: 'sd-card__head' }, [h('span', { text: 'DIRECTOR RATING' })]),
    h('div', { class: 'sd-rating__body' }, [
      gauge.el,
      h('div', { class: 'sd-mult', attrs: { 'data-mult': '1' } }, [
        h('div', { class: 'sd-mult__label', text: 'STREAK' }),
        multEl,
        h('div', { class: 'sd-mult__pips' }, pips),
        h('div', { class: 'sd-mult__meta' }, [streakEl, h('span', { text: ' IN A ROW' })]),
        h('div', { class: 'sd-points' }, [h('span', { class: 'sd-points__label', text: 'PTS' }), pointsEl]),
      ]),
    ]),
  ]);
  const multBox = ratingCard.querySelector('.sd-mult') as HTMLElement;

  const catRows = new Map<GradeCategory, { fill: HTMLElement; val: HTMLElement; row: HTMLElement; last: number }>();
  const catCard = h('section', { class: 'sd-card sd-cats' }, [
    h('div', { class: 'sd-card__head' }, [h('span', { text: 'BREAKDOWN' })]),
    ...CATEGORY_ORDER.map((c) => {
      const fill = h('div', { class: 'sd-cat__fill' });
      const val = h('span', { class: 'sd-cat__val', text: '—' });
      const row = h('div', { class: 'sd-cat', dataset: { cat: c } }, [
        h('span', { class: 'sd-cat__label', text: CATEGORY_LABELS[c] }), h('div', { class: 'sd-cat__track' }, [fill]), val,
      ]);
      catRows.set(c, { fill, val, row, last: -1 });
      return row;
    }),
  ]);

  const boothList = h('div', { class: 'sd-booth__list' });
  const boothEmpty = h('div', { class: 'sd-booth__empty' }, [h('span', { class: 'sd-booth__mic' }), h('span', { text: 'BOOTH QUIET' })]);
  const boothCount = h('span', { class: 'sd-booth__count' });
  const boothCard = h('section', { class: 'sd-card sd-booth' }, [
    h('div', { class: 'sd-card__head' }, [h('span', { text: 'TALKBACK · BOOTH' }), boothCount]),
    boothEmpty, boothList,
  ]);
  const side = h('aside', { class: 'sd-side' }, [matchCard, ratingCard, catCard, boothCard]);

  // ---------------------------------------------------------------- hints
  const hints = h('footer', { class: 'sd-hints' });
  const renderHints = () => {
    const pairs: [string, string][] = punchMode === 'hot'
      ? [['1–9', 'PUNCH'], ['⇧ 1–9', 'PREVIEW'], ['SPACE', 'TAKE'], ['M', 'MIX']]
      : [['1–9', 'PREVIEW'], ['SPACE', 'TAKE'], ['M', 'MIX'], ['⇧ 1–9', 'PUNCH']];
    pairs.push(['R', 'REPLAY'], ['Q', 'LOWER 3RD'], ['W', 'STATS'], ['E', 'POSS.'], ['S', 'BUG'], ['A', 'CLEAR'], ['TAB', 'BOOTH'], ['ESC', 'PAUSE']);
    hints.textContent = '';
    for (const [k, l] of pairs) hints.append(h('span', { class: 'sd-hint' }, [h('kbd', { text: k }), h('span', { text: l })]));
  };
  renderHints();

  el.append(plate, top, pgm.root, programOverlay, toastLayer, bigLayer, pvw.root, deck, wallEl, side, hints);
  root.append(el);
  buildWall();

  // ---------------------------------------------------------------- layout
  const relayout = () => {
    const W = el.clientWidth || root.clientWidth || window.innerWidth;
    const H = el.clientHeight || root.clientHeight || window.innerHeight;
    geo = computeConsoleLayout(W, H, cams.length);
    el.style.fontSize = `${geo.u}px`;
    toggleClass(el, 'is-compact', geo.compact);
    place(pgm.root, bezelRect(geo.program, geo.bezel, geo.umdBig));
    place(pvw.root, bezelRect(geo.preview, geo.bezel, geo.umdBig));
    for (const m of [pgm, pvw]) { m.root.style.setProperty('--bz', `${geo.bezel}px`); m.root.style.setProperty('--umd', `${geo.umdBig}px`); }
    place(programOverlay, geo.program);
    place(toastLayer, geo.program);
    place(bigLayer, geo.program);
    place(deck, geo.deck);
    place(side, geo.side);
    place(top, geo.top);
    place(hints, geo.hints);
    tiles.forEach((t, i) => {
      const r = geo!.wall[i]!;
      place(t.mon.root, bezelRect(r, geo!.bezel, geo!.umdTile));
      t.mon.root.style.setProperty('--bz', `${geo!.bezel}px`);
      t.mon.root.style.setProperty('--umd', `${geo!.umdTile}px`);
    });
    plate.style.clipPath = `path(evenodd, '${holesPath(W, H, [geo.program, geo.preview, ...geo.wall])}')`;
    const off = el.getBoundingClientRect();
    const shift = (r: Rect): Rect => ({ x: r.x + off.left, y: r.y + off.top, w: r.w, h: r.h });
    layoutCache = {
      program: shift(geo.program),
      preview: shift(geo.preview),
      wall: tiles.map((t, i) => ({ camId: t.cam.id, rect: shift(geo!.wall[i]!) })),
    };
    layoutListeners.forEach((fn) => fn(layoutCache!));
  };
  const ro = new ResizeObserver(() => relayout());
  ro.observe(el);
  relayout();

  // ---------------------------------------------------------------- per-frame state
  let lastText = -Infinity;
  let lastSample = -Infinity;
  const history: number[] = [];
  let peakViewers = 0, bestStreak = 0, lastMult = 1;
  let lastConfigSeed: number | null = null;
  let playerNames = new Map<string, string>();
  const booth = new Map<number, { el: HTMLElement; bar: HTMLElement; time: HTMLElement; resolvedAt: number | null; state: string }>();
  let focusedId: number | null = null;
  let lastRequests: CommentatorRequest[] = [];

  function flash(target: HTMLElement) {
    target.classList.remove('is-flash');
    void target.offsetWidth;
    target.classList.add('is-flash');
  }

  function camLabel(id: string): string {
    const c = cams.find((x) => x.id === id);
    return c ? (c.id === c.label ? c.label : `${c.id} · ${c.label}`) : id;
  }

  function wantText(r: CommentatorRequest): { key: string; what: string } {
    const w = r.wants;
    const who = w.subjectId ? playerNames.get(w.subjectId) ?? '' : '';
    if (w.replay) return { key: 'R', what: `REPLAY${who ? ` · ${who}` : ''}` };
    if (w.graphic) return { key: WANT_GRAPHIC_KEY[w.graphic] ?? '', what: `${GRAPHIC_NAMES[w.graphic] ?? w.graphic.replace(/_/g, ' ').toUpperCase()}${who ? ` · ${who}` : ''}` };
    if (w.camKind) {
      const cam = cams.find((c) => c.kind === w.camKind);
      return { key: cam?.hotkey ?? '', what: cam ? (cam.id === cam.label ? cam.label : `${cam.id} ${cam.label}`) : w.camKind.replace(/_/g, ' ').toUpperCase() };
    }
    if (who) return { key: '', what: `SHOW ${who}` };
    return { key: '', what: '—' };
  }

  function updateBooth(view: ConsoleView, now: number) {
    const t = view.snapshot.t;
    const seen = new Set<number>();
    lastRequests = view.requests;
    let active = 0;
    for (const r of view.requests) {
      const live = !r.resolved && t < r.expiresAt;
      let entry = booth.get(r.id);
      if (!entry && !live) continue;
      seen.add(r.id);
      if (!entry) {
        const wt = wantText(r);
        const bar = h('div', { class: 'sd-req__bar' });
        const time = h('span', { class: 'sd-req__time' });
        const reqEl = h('div', { class: 'sd-req', dataset: { id: String(r.id) }, attrs: { role: 'button', title: 'Focus this request (Tab)' }, on: { click: () => { focusedId = r.id; actions.emit('boothFocus', r.id); } } }, [
          h('div', { class: 'sd-req__quote', text: `“${r.text}”` }),
          h('div', { class: 'sd-req__want' }, [
            wt.key ? h('kbd', { text: wt.key }) : null,
            h('span', { class: 'sd-req__what', text: wt.what }),
            time,
          ]),
          h('div', { class: 'sd-req__track' }, [bar]),
          h('div', { class: 'sd-req__stamp' }),
        ]);
        boothList.prepend(reqEl);
        entry = { el: reqEl, bar, time, resolvedAt: null, state: 'live' };
        booth.set(r.id, entry);
      }
      if (live) {
        active++;
        const total = Math.max(0.001, r.expiresAt - r.t);
        const frac = Math.max(0, Math.min(1, (r.expiresAt - t) / total));
        entry.bar.style.transform = `scaleX(${frac.toFixed(4)})`;
        toggleClass(entry.el, 'is-urgent', frac < 0.3);
        setText(entry.time, `${Math.max(0, r.expiresAt - t).toFixed(1)}s`);
        toggleClass(entry.el, 'is-focus', focusedId === r.id);
      } else if (entry.resolvedAt === null) {
        const state = r.resolved ?? 'missed';
        entry.resolvedAt = now;
        entry.state = state;
        entry.el.classList.add(`is-${state}`);
        const stamp = entry.el.querySelector('.sd-req__stamp')!;
        stamp.textContent = state === 'hit' ? '✓ ON IT' : state === 'late' ? '◐ LATE' : '✗ MISSED';
      }
    }
    for (const [id, e] of booth) {
      const gone = !seen.has(id);
      if (gone && e.resolvedAt === null) { e.resolvedAt = now; e.el.classList.add('is-missed'); }
      if (e.resolvedAt !== null && now - e.resolvedAt > 1800) {
        if (!e.el.classList.contains('is-leaving')) {
          e.el.classList.add('is-leaving');
          setTimeout(() => e.el.remove(), 350);
        }
        if (now - e.resolvedAt > 2200) booth.delete(id);
      }
    }
    if (focusedId !== null && !view.requests.some((r) => r.id === focusedId && !r.resolved && t < r.expiresAt)) focusedId = null;
    toggleClass(boothEmpty, 'is-hidden', booth.size > 0);
    setText(boothCount, active ? `${active} OPEN` : '');
    toggleClass(boothCard, 'has-live', active > 0);
  }

  function updateTally(d: DirectorState) {
    const replay = d.replay.active;
    const onAirId = replay ? d.replay.camId : d.programCamId;
    for (const t of tiles) {
      const isPgm = t.cam.id === onAirId;
      toggleClass(t.mon.root, 'is-pgm', isPgm && !replay);
      toggleClass(t.mon.root, 'is-replay', isPgm && replay);
      toggleClass(t.mon.root, 'is-pvw', t.cam.id === d.previewCamId && !isPgm);
    }
    toggleClass(pgm.root, 'is-replay', replay);
    setText(pgm.title, replay ? `REPLAY · ${camLabel(onAirId)}` : camLabel(d.programCamId));
    setText(pvw.title, camLabel(d.previewCamId));
    toggleClass(takeBtn, 'is-armed', d.previewCamId !== d.programCamId && !replay);
    toggleClass(mixBtn, 'is-armed', d.previewCamId !== d.programCamId && !replay);
    toggleClass(replayBtn, 'is-on', replay);
    const onAirKinds = new Set(d.graphics.filter((g) => g.hiddenAt === undefined).map((g) => g.kind));
    for (const g of gfxButtons) toggleClass(g.el, 'is-on', g.def.kinds.some((k) => onAirKinds.has(k)));
    // replay panel
    const rp = d.replay;
    toggleClass(replayPanel, 'is-replay', replay);
    setText(rpState, replay ? 'REPLAY' : 'LIVE');
    if (replay) {
      const span = Math.max(0.001, rp.toT - rp.fromT);
      const p = Math.max(0, Math.min(1, (rp.headT - rp.fromT) / span));
      rpFill.style.transform = `scaleX(${p.toFixed(4)})`;
      rpHead.style.left = `${(p * 100).toFixed(2)}%`;
      setText(rpSpeed, `${rp.speed.toFixed(2)}×`);
      setText(rpCam, rp.camId);
    } else {
      rpFill.style.transform = 'scaleX(0)';
      rpHead.style.left = '0%';
      setText(rpSpeed, '1.00×');
      setText(rpCam, 'EVS READY');
    }
  }

  function updateConfig(cfg: MatchConfig) {
    if (lastConfigSeed === cfg.seed) return;
    lastConfigSeed = cfg.seed;
    homeChip.style.background = `linear-gradient(135deg, ${cfg.home.primaryColor} 0 60%, ${cfg.home.secondaryColor} 60%)`;
    awayChip.style.background = `linear-gradient(135deg, ${cfg.away.primaryColor} 0 60%, ${cfg.away.secondaryColor} 60%)`;
    homeName.textContent = cfg.home.shortName;
    awayName.textContent = cfg.away.shortName;
    homeName.title = cfg.home.name; awayName.title = cfg.away.name;
    compEl.textContent = cfg.competition.toUpperCase();
    venueEl.textContent = `${cfg.venue.toUpperCase()} · ${WEATHER_LABELS[cfg.weather]} · ${TIME_LABELS[cfg.timeOfDay]}`;
    playerNames = new Map<string, string>();
    const add = (p: PlayerProfile) => playerNames.set(p.id, p.shortName);
    for (const t of [cfg.home, cfg.away]) { t.players.forEach(add); t.bench.forEach(add); }
  }

  function update(view: ConsoleView) {
    const now = performance.now();
    const { snapshot: s, report: r, director: d } = view;
    updateConfig(view.config);
    updateTally(d);
    updateBooth(view, now);
    intensityFill.style.transform = `scaleX(${Math.max(0, Math.min(1, s.intensity)).toFixed(3)})`;
    toggleClass(intensityFill, 'is-hot', s.intensity > 0.5);

    peakViewers = Math.max(peakViewers, r.viewers);
    bestStreak = Math.max(bestStreak, r.streak);
    if (now - lastSample > 1000 || history.length === 0) {
      lastSample = now;
      history.push(r.viewers);
      if (history.length > 90) history.shift();
      spark.set(history);
      viewersOdo.set(r.viewers); // once a second: a constantly-rolling odometer is unreadable
    }

    if (now - lastText < 100) return; // ~10 Hz for text/bars
    lastText = now;

    setText(homeScore, String(s.score.home));
    setText(awayScore, String(s.score.away));
    clock.set(clockText(s.matchClock));
    setText(halfEl, s.half === 1 ? '1ST' : '2ND');
    setText(phaseEl, PHASE_LABELS[s.phase]);
    phaseEl.dataset.phase = s.phase;
    const live = s.phase !== 'pre_match' && s.phase !== 'half_time' && s.phase !== 'full_time';
    toggleClass(onAir, 'is-live', live);

    gauge.set(r.rating, r.letter, LETTER_COLORS[r.letter]);
    const { mult, progress } = streakMultiplier(r.streak);
    if (mult !== lastMult) {
      multBox.dataset.mult = String(mult);
      setText(multEl, `×${mult}`);
      if (mult > lastMult) flash(multBox);
      lastMult = mult;
    }
    const lit = mult >= 4 ? 5 : Math.round(progress * 5);
    pips.forEach((p, i) => toggleClass(p, 'on', i < lit));
    setText(streakEl, String(r.streak));
    setText(pointsEl, Math.round(r.total).toLocaleString('en-GB'));

    for (const c of CATEGORY_ORDER) {
      const row = catRows.get(c)!;
      const v = Math.round(r.byCategory[c] ?? 0);
      if (v === row.last) continue;
      row.last = v;
      row.fill.style.transform = `scaleX(${(v / 100).toFixed(3)})`;
      row.fill.style.background = barColor(v);
      setText(row.val, String(v));
    }

    const ref = history.length > 4 ? history[history.length - 5]! : history[0] ?? r.viewers;
    const delta = r.viewers - ref;
    const dir = Math.abs(delta) < r.viewers * 0.002 ? 'flat' : delta > 0 ? 'up' : 'down';
    viewersArrow.dataset.dir = dir;
    setText(viewersArrow, dir === 'down' ? '▼' : dir === 'up' ? '▲' : '■');
    setText(viewersDelta, dir === 'flat' ? '' : `${delta > 0 ? '+' : '−'}${formatCompact(Math.abs(delta))}`);
    viewersDelta.dataset.dir = dir;
  }

  // ---------------------------------------------------------------- toasts
  const TOAST_MAX = 4;
  function toast(f: GradeFeedback, o: { big?: boolean } = {}) {
    const label = f.label.toUpperCase();
    const bigBad = f.severity === 'terrible' && /MISS/.test(label);
    const bigGood = f.severity === 'great' && /GOAL/.test(label);
    if (o.big || bigBad || bigGood) {
      const tone = f.severity === 'great' || f.severity === 'good' ? 'great' : 'terrible';
      const b = h('div', { class: `sd-bigtoast sev-${tone}` }, [
        h('div', { class: 'sd-bigtoast__text', text: label, dataset: { text: label } }),
        f.delta ? h('div', { class: 'sd-bigtoast__delta', text: `${f.delta > 0 ? '+' : '−'}${Math.abs(Math.round(f.delta))}` }) : null,
      ]);
      bigLayer.append(b);
      if (tone === 'terrible' && !prefersReducedMotion()) { pgm.root.classList.remove('is-shake'); void pgm.root.offsetWidth; pgm.root.classList.add('is-shake'); }
      if (tone === 'terrible') { flash(plate); el.classList.add('is-alarm'); setTimeout(() => el.classList.remove('is-alarm'), 900); }
      setTimeout(() => b.classList.add('is-out'), 2100);
      setTimeout(() => b.remove(), 2600);
      return;
    }
    const t = h('div', { class: `sd-toast sev-${f.severity}` }, [
      h('span', { class: 'sd-toast__cat', text: CATEGORY_LABELS[f.category] }),
      h('span', { class: 'sd-toast__label', text: label }),
      f.delta ? h('span', { class: 'sd-toast__delta', text: `${f.delta > 0 ? '+' : '−'}${Math.abs(Math.round(f.delta))}` }) : null,
    ]);
    toastLayer.prepend(t);
    const all = toastLayer.querySelectorAll('.sd-toast:not(.is-out)');
    for (let i = TOAST_MAX; i < all.length; i++) { const old = all[i] as HTMLElement; old.classList.add('is-out'); setTimeout(() => old.remove(), 250); }
    const life = f.severity === 'info' ? 1400 : 1900;
    setTimeout(() => t.classList.add('is-out'), life);
    setTimeout(() => t.remove(), life + 300);
  }

  // ---------------------------------------------------------------- misc API
  const keyMap: Record<string, () => HTMLElement | undefined> = {
    SPACE: () => takeBtn, M: () => mixBtn, R: () => replayBtn,
    Q: () => gfxButtons[0]!.el, W: () => gfxButtons[1]!.el, E: () => gfxButtons[2]!.el, S: () => gfxButtons[3]!.el, A: () => gfxButtons[4]!.el,
    ESCAPE: () => pauseBtn,
  };
  function indicate(key: string) {
    const k = key.toUpperCase().replace(/^KEY/, '').replace(/^DIGIT/, '');
    const target = keyMap[k]?.() ?? tiles.find((t) => t.cam.hotkey === k)?.mon.root;
    if (target) flash(target);
  }

  function focusBooth(dir: 1 | -1): number | null {
    const open = lastRequests.filter((r) => !r.resolved).sort((a, b) => a.expiresAt - b.expiresAt);
    if (!open.length) { focusedId = null; return null; }
    const i = open.findIndex((r) => r.id === focusedId);
    focusedId = open[(i + dir + open.length) % open.length]!.id;
    return focusedId;
  }

  return {
    el, actions, programOverlay,
    layout: () => { if (!layoutCache) relayout(); return layoutCache!; },
    onLayout(fn) { layoutListeners.add(fn); return () => layoutListeners.delete(fn); },
    update, toast, indicate, focusBooth,
    focusedRequest: () => focusedId,
    setCams(next) { cams = next.slice(); buildWall(); relayout(); },
    setPunchMode(m) { punchMode = m; renderHints(); buildWall(); relayout(); },
    stats: () => ({ peakViewers, bestStreak, viewerHistory: history.slice() }),
    reset() {
      history.length = 0; peakViewers = 0; bestStreak = 0; lastMult = 1; lastConfigSeed = null; focusedId = null;
      toastLayer.textContent = ''; bigLayer.textContent = ''; boothList.textContent = ''; booth.clear();
      catRows.forEach((r) => (r.last = -1));
    },
    destroy() { ro.disconnect(); el.remove(); layoutListeners.clear(); },
  };
}
